import React, { useState } from 'react';
import styled from 'styled-components';
import { BreakpointValues, Grid, Type } from '../../../app/StyleGuide';
import { LinkStyleButton } from '../../../components/Buttons';
import { DynamicColumnLayout } from '../../../components/DynamicColumnLayout';
import { Container, FullWidthCard, RaisedContainer } from '../../../components/Layout';
import windowService from '../../../services/windowService';
import { AgeCategory, BrandCode } from '../../curriculum';
import { OverviewContent, OverviewContentSection } from '../models/overviewContent';
import { OverviewAdvancePlanningSection } from './OverviewAdvancePlanningSection';
import { OverviewSessionsSection } from './OverviewSessionsSection';
import { OverviewVolumeDetailsSection } from './OverviewVolumeDetailsSection';
import { PeriodNavigation } from './PeriodNavigation';

const MainContainer = styled(FullWidthCard)`
  margin-bottom: 0;
  padding: ${Grid._4} 0 ${Grid._12} 0;
  background: none;
`;

const SectionContentContainer = styled(RaisedContainer)`
  padding: ${Grid._4};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${Grid._3};
  button {
    font-size: ${Type.Scale._1};
    padding: 8px;
  }
`;

const ContentContainer = styled.div`
  --gap: ${Grid._4};

  display: flex;
  column-gap: var(--gap);

  div[data-column] {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    row-gap: var(--gap);

    &[data-column='1'] {
      flex-shrink: 0;
      min-width: 480px;
      max-width: 520px;
    }
  }
`;

const Header = ({ heading, viewAllUrl }: { heading: string; viewAllUrl?: string }) => (
  <HeaderRow>
    <h4>{heading}</h4>
    {viewAllUrl && <LinkStyleButton onClick={() => windowService.redirectTo(viewAllUrl)}>View All</LinkStyleButton>}
  </HeaderRow>
);

type Props = {
  brandCode: BrandCode;
  ageCategory: AgeCategory;
  curriculumId: string;
  pages: OverviewContent[];
};

export const OverviewContentPresenter = ({ brandCode, ageCategory, curriculumId, pages }: Props) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(pages.length >= 2 ? 1 : 0);
  const { volume, planningItems, sessions, additionalSections } = pages[currentPageIndex];

  const renderAdditionalSections = (
    sections: OverviewContentSection[],
    location: string,
    props?: React.PropsWithoutRef<object>
  ) => {
    return sections
      .filter(section => section.location === location)
      .map(section => (
        <div key={section.heading} {...props}>
          <Header heading={section.heading} viewAllUrl={section.viewAllUrl} />
          <SectionContentContainer>
            <section.component {...section.props} />
          </SectionContentContainer>
        </div>
      ));
  };

  return (
    <MainContainer>
      <Container>
        <PeriodNavigation pages={pages} startingIndex={currentPageIndex} onNavigate={setCurrentPageIndex} />

        <ContentContainer>
          <DynamicColumnLayout
            key={currentPageIndex}
            breakpoints={[
              { width: 0, columns: 1 },
              { width: BreakpointValues.screen_md, columns: 2 },
            ]}
          >
            <div>
              <Header heading="Month at a Glance" />
              <SectionContentContainer>
                <OverviewVolumeDetailsSection volume={volume} />
              </SectionContentContainer>
            </div>
            <div>
              <Header heading="Sessions" />
              <SectionContentContainer>
                <OverviewSessionsSection
                  key={volume._id}
                  brandCode={brandCode}
                  ageCategory={ageCategory}
                  volumeName={volume.name}
                  sessions={sessions}
                />
              </SectionContentContainer>
            </div>
            <>{renderAdditionalSections(additionalSections, 'main', { style: { order: 4 } })}</>
            <div style={{ order: 3 }}>
              <Header
                heading="Advance Planning"
                viewAllUrl={`/manage-bible-study/advance-planning/${brandCode}/${ageCategory}/${curriculumId}`}
              />
              <SectionContentContainer>
                <OverviewAdvancePlanningSection planningItems={planningItems} />
              </SectionContentContainer>
            </div>
            <>{renderAdditionalSections(additionalSections, 'sidebar')}</>
          </DynamicColumnLayout>
        </ContentContainer>
      </Container>
    </MainContainer>
  );
};
